import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './GameUI.css';
import coffeeIcon from './assets/icons/bmc-logo-yellow.png';
import logo from './assets/icons/logo-no-background.png';
import facebookIcon from './assets/icons/facebook-brands-solid.svg';
import xIcon from './assets/icons/x-twitter-brands-solid.svg';
import linkIcon from './assets/icons/copy-regular.svg';
import statsIcon from './assets/icons/chart-simple-solid.svg';
import triviaIcon from './assets/icons/masks-theater-solid.svg';
import emailIcon from './assets/icons/envelope-solid.svg';
import partyIcon from './assets/icons/confetti-bold.svg';
import sadIcon from './assets/icons/heart-crack-solid.svg';
import challengeIcon from './assets/icons/lightning-bold.svg';
import leaderboardIcon from './assets/icons/ranking-bold.svg';

function getTimeUntilMidnight() {
	const now = new Date();
	const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
	const diff = midnight - now;
	if (diff <= 0) return '00:00:00';

	const hours = Math.floor(diff / (1000 * 60 * 60));
	const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
	const seconds = Math.floor((diff % (1000 * 60)) / 1000);

	const hh = String(hours).padStart(2, '0');
	const mm = String(minutes).padStart(2, '0');
	const ss = String(seconds).padStart(2, '0');
	return `${hh}:${mm}:${ss}`;
}

function GameUI({
	gameOver,
	success,
	gameData,
	currentQuoteIndex,
	highestIndex,
	userGuess,
	attempts,
	searchResults,
	handleInputChange,
	handleGuess,
	resetGame,
	setUserGuess,
	setCurrentQuoteIndex,
	handleShareResults,
	handleEmailCopy,
	emailCopied,
	handleTwitterShare,
	handleFacebookShare,
	handleCopyLink,
	copied,
	shareButtonText,
}) {
	const [isDarkMode, setIsDarkMode] = useState(false);
	const [showDropdown, setShowDropdown] = useState(false);
	const [showFinalPoster, setShowFinalPoster] = useState(true);

	const navigate = useNavigate();

	const toggleTheme = () => {
		setIsDarkMode((prevMode) => !prevMode);
	};

	const handleShowPoster = () => {
		setShowFinalPoster((prev) => !prev);
	};

	/**
	 * Only allow navigation to blocks up to `highestIndex`.
	 */
	const handleBlockClick = (index) => {
		if (index <= highestIndex) {
			setCurrentQuoteIndex(index);
		}

		if (gameOver) {
			setShowFinalPoster(false); // Hide final poster
			setCurrentQuoteIndex(index); // Set the quote/sketch based on the clicked block
		}
	};

	const handleDropdownSelection = (result) => {
		setUserGuess(result);
		setShowDropdown(false);
	};

	const [timeLeft, setTimeLeft] = useState(getTimeUntilMidnight());
	useEffect(() => {
		const timer = setInterval(() => {
			setTimeLeft(getTimeUntilMidnight());
		}, 1000);
		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		if (searchResults.length > 0) {
			setShowDropdown(true);
		} else {
			setShowDropdown(false);
		}
	}, [searchResults]);

	// Might be `null` if gameData or quotes aren’t ready yet
	if (!gameData || !gameData.quotes[currentQuoteIndex]) {
		return <div>Loading...</div>;
	}

	return (
		<div className={isDarkMode ? 'dark-mode' : 'light-mode'}>
			<div className="header-container">
				<img
					src={triviaIcon}
					alt="Movie Quotes Guesser Trivia Icon"
					className="trivia-icon"
					onClick={() => navigate('/trivia')}
				/>
				<div className="logo-box">
					<img src={logo} alt="Movie Quotes Guesser Logo" className="logo" />
					<h3 className="logo-text">Movie Quotes Guesser</h3>
				</div>
				<div className="right-section">
					<div className="challenge-container">
						<img
							src={challengeIcon}
							alt="Challenge Icon"
							className="challenge-icon"
							onClick={() => navigate('/challenge')}
						/>
						<div className="new-badge">New</div>
					</div>
					<img
						src={leaderboardIcon}
						alt="Leaderboard Icon"
						className="leaderboard-icon"
						onClick={() => navigate('/leaderboard/Nolan')}
					/>
					<img src={statsIcon} alt="Stats Icon" className="stats-icon" onClick={() => navigate('/stats')} />
				</div>
			</div>

			<div className="game-container">
				<div>
					{gameOver && showFinalPoster ? (
						<div className="final-poster-container">
							<a
								href={gameData.amazonLink || 'https://amzn.to/4hN8SED'}
								target="_blank"
								rel="noopener noreferrer"
								className="poster-link"
							>
								<div className={`poster-transition ${gameData.poster ? 'loaded' : ''}`}>
									<img
										src={gameData.poster}
										alt="Final Movie Poster"
										className="final-poster-image"
										onLoad={() => {
											// Optional: Add any additional logic when poster loads
										}}
									/>
									<span className="tooltip">Buy this movie on Amazon!</span>
								</div>
							</a>
						</div>
					) : (
						<div className="poster-container">
							<img
								src={gameData.quotes[currentQuoteIndex].sketch}
								alt={`Sketch for the game`}
								className="poster-image"
							/>
						</div>
					)}
				</div>
				<main className="main-content">
					{gameOver ? (
						<div className="game-over">
							<div className="progress-banner-container">
								<div className="progress-bar">
									{gameData.quotes.map((_, index) => {
										const isActive = index === currentQuoteIndex;
										const isVisible = index <= highestIndex;
										// Check if there is an attempt at this index and if it was a series match
										const isSeriesMatch = attempts[index] && attempts[index].isSeriesMatch;
										return (
											<div
												key={index}
												className={`progress-block ${
													isActive
														? 'active'
														: isSeriesMatch
														? 'series-match'
														: isVisible
														? 'completed'
														: ''
												}`}
												onClick={() => handleBlockClick(index)}
											/>
										);
									})}
								</div>
							</div>

							{!showFinalPoster && (
								<blockquote className="quote-text">
									{gameData.quotes[currentQuoteIndex].text}
								</blockquote>
							)}

							<div className="audio-box">
								<audio
									controls
									src={gameData.quotes[currentQuoteIndex].audio}
									className="audio-player"
									controlsList="nodownload noplaybackrate"
								/>
							</div>

							<h2 className="status-message">
								{success ? (
									<>
										<img src={partyIcon} alt="Party Icon" className="game-status-icon" /> You
										guessed it right!
									</>
								) : (
									<>
										<img src={sadIcon} alt="Sad Icon" className="game-status-icon" /> Game Over!
									</>
								)}
							</h2>

							<h3 className="movie-title">
								The movie was: <strong>{gameData.title}</strong>
							</h3>
							<h3>Come back for a new puzzle in: {timeLeft}</h3>
							<div className="game-over-buttons">
								<button className="share-result-button" onClick={handleShareResults}>
									{shareButtonText}
								</button>
								<button className="show-poster-button" onClick={handleShowPoster}>
									{showFinalPoster ? 'Show Scenes' : 'Show Poster'}
								</button>
							</div>
						</div>
					) : (
						<div className="game-active">
							<div className="progress-bar">
								{gameData.quotes.map((_, index) => {
									const isActive = index === currentQuoteIndex;
									const isVisible = index <= highestIndex;
									const isSeriesMatch = attempts[index] && attempts[index].isSeriesMatch;
									return (
										<div
											key={index}
											className={`progress-block ${
												isActive
													? 'active'
													: isSeriesMatch
													? 'series-match'
													: isVisible
													? 'completed'
													: ''
											}`}
											onClick={() => handleBlockClick(index)}
										/>
									);
								})}
							</div>
							<blockquote className="quote-text">{gameData.quotes[currentQuoteIndex].text}</blockquote>
							<div className="audio-box flex-start-on-mobile">
								<audio
									controls
									src={gameData.quotes[currentQuoteIndex].audio}
									className="audio-player wider-on-mobile"
									controlsList="nodownload noplaybackrate"
								/>
							</div>

							<div className="input-group-box flex-start-on-mobile">
								<div className="input-group wider-on-mobile">
									<input
										type="text"
										className="input-text"
										value={userGuess}
										onChange={handleInputChange}
										placeholder="Guess the movie title or click Submit to skip..."
										onFocus={() => setShowDropdown(true)}
										disabled={gameOver}
									/>
									<button className="btn-primary" onClick={handleGuess} disabled={gameOver}>
										Submit
									</button>
									{showDropdown && searchResults.length > 0 && (
										<ul className="search-results">
											{searchResults.map((result, index) => (
												<li
													key={index}
													className="search-result-item"
													onClick={() => handleDropdownSelection(result)}
												>
													{result}
												</li>
											))}
										</ul>
									)}
								</div>
							</div>

							<div className="guess-container">
								<div className="previous-guesses">
									{attempts.map((attemptObj, index) => (
										<div key={index} className="guess-item">
											✗ {attemptObj.guess}
										</div>
									))}
								</div>
							</div>
						</div>
					)}
				</main>
			</div>

			{/* Footer section */}
			<div className="footer-container">
				<div className="email-box">
					<img src={emailIcon} alt="Copy email icon" className="email-icon" onClick={handleEmailCopy} />
					{emailCopied && <span className="copied-text-email">Email Copied</span>}
				</div>
				<div className="share-buttons">
					<img src={xIcon} alt="Share us on X" className="share-image" onClick={handleTwitterShare} />

					<img
						src={facebookIcon}
						alt="Share us on Facebook"
						className="share-image"
						onClick={handleFacebookShare}
					/>

					<img src={linkIcon} alt="Copy our website link" className="share-image" onClick={handleCopyLink} />
					{copied && <span className="copied-text">Copied</span>}
				</div>

				<a
					href="https://buymeacoffee.com/moviequotesguesser"
					target="_blank"
					rel="noopener noreferrer"
					className="support-button"
				>
					<img src={coffeeIcon} alt="Buy Me A Coffee" className="support-image" />
				</a>
			</div>
		</div>
	);
}

export default GameUI;
